import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import {
  getContextMenuItems,
  StatefulInput,
  StudioContext,
  UserContext,
  OwnershipContextSelector,
} from 'venn-components';
import type { DropMenuItem } from 'venn-ui-kit';
import { Headline3 } from 'venn-ui-kit';
import { StyledLabel, GapWrapper } from '../shared';
import { getContext, useHasFF } from 'venn-utils';
import { useRecoilState } from 'recoil';
import { analysisViewOwnerContextIdState } from 'venn-state';

const NAME_INPUT = 'project-name-input';
const GLOBAL_CONTEXT = 'Global';

const Details = () => {
  const hasNewPermissions = useHasFF('new_permissions_ff');
  const hasCustomizationEnhancements = useHasFF('rl_customization_enhancements_ff');
  const [ownerContextId, setOwnerContextId] = useRecoilState(analysisViewOwnerContextIdState);
  const { onChangeReportName, reportNameValue, isDuplicateReportName } = useContext(StudioContext);
  const { currentContext, profileSettings, hasPermissionForResource } = useContext(UserContext);

  const items: DropMenuItem<string>[] = useMemo(
    () => getContextMenuItems(profileSettings?.availableContexts, currentContext, 'EDIT_ANALYSIS_VIEW') ?? [],
    [currentContext, profileSettings?.availableContexts],
  );

  const canEditView = hasPermissionForResource('EDIT_ANALYSIS_VIEW', { ownerContextId });

  // Default to global context if not assigned
  const globalContext = items.find((i) => i.label === GLOBAL_CONTEXT)?.value;

  return (
    <GapWrapper>
      <Headline3>Details</Headline3>
      <LabelWrapper>
        <StyledLabel htmlFor={NAME_INPUT}>Document Name</StyledLabel>
      </LabelWrapper>

      <StatefulInput
        placeholder="Document Name"
        value={reportNameValue}
        onChange={onChangeReportName}
        className="qa-save-name-input border-venn-dark-blue w-full"
        selectOnFocus
        error={isDuplicateReportName}
        id={NAME_INPUT}
      />
      {hasNewPermissions && (
        <GapWrapper>
          {canEditView ? (
            <OwnershipContextSelector
              ownerContextId={ownerContextId ?? globalContext}
              setOwnerContextId={setOwnerContextId}
              requiredAction="EDIT_ANALYSIS_VIEW"
              customLabel={hasCustomizationEnhancements ? 'Ownership' : undefined}
            />
          ) : (
            getContext(ownerContextId ?? '', profileSettings?.availableContexts ?? [])?.context.name
          )}
        </GapWrapper>
      )}
    </GapWrapper>
  );
};

export default Details;

const LabelWrapper = styled.div`
  display: flex;
  column-gap: 8px;
  font-size: 14px;
`;
